import { Injectable } from '@angular/core';

import { MessageService } from 'primeng/api';
import { Observable, catchError, map, throwError } from 'rxjs';

import { MediaResultModel, MediaServiceErrorCodesEnum } from '../../../../../api/mediaService/mediaResults.api.model';
import { MediaServiceApi } from '../../../../../api/mediaService/media-service.api';
import { MediaServiceAccessTypeEnum } from '../../../../../api/mediaService/enums/media-service-access-type.enum';

import { ConfirmDialogComponent } from '../../../..//Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../..//Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { getReadableFileSize } from '../../../../../Core/utils';


@Injectable()
export class CKUploadService {

    readonly IMAGE_FILE_TYPE_REGEX = /image\/(gif|jpe?g|png)/;
    readonly MAX_FILE_SIZE_BYTES = 1000000;


    constructor(
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public mediaServiceApi: MediaServiceApi,
        public messageService: MessageService
    ) {}

    /**
     * Handles custom File uploade button
     */
    public handleFileInput(file: File): Observable<string> {
        if (!file.type.match(this.IMAGE_FILE_TYPE_REGEX)) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileTypeHeader'),
                detail: this.localizationService.transform(
                    'PageBuilder.error.fileTypeMessage',
                    {
                        types: 'gif, jpg, png'
                    }
                )
            });
            return throwError(() => new Error('Invalid file type'));
        }

        if (this.MAX_FILE_SIZE_BYTES < file.size) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileSizeHeader'),
                detail: this.localizationService.transform('PageBuilder.error.fileSizeMessage.specific', {
                    size: getReadableFileSize(this.MAX_FILE_SIZE_BYTES)
                })
            });
            return throwError(() => new Error('File is too large'));
        }

        return this.mediaServiceApi.upload([file], MediaServiceAccessTypeEnum.PUBLIC).pipe(
            catchError(error => {
                if (error.status === 429) {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.heading'),
                        detail: this.localizationService.transform('Common.mediaService.fileUpload.notEnoughSpace.message', { name: file.name })
                    });
                }
                throw error;
            }),
            map(result => {
                const imageMediaResult: MediaResultModel = result?.mediaResults?.find(mediaResult => mediaResult?.media?.name === file.name);

                if (imageMediaResult) {
                    const url = imageMediaResult?.media?.url;
                    const errorCode = imageMediaResult?.codedError?.code;

                    if (errorCode) {
                        this.handleErrorCode(errorCode);
                        throw new Error(errorCode);
                    }

                    return url;
                }

                throwError(() => new Error());
            })
        );
    }

    handleErrorCode(errorCode: MediaServiceErrorCodesEnum) {
        if (errorCode === MediaServiceErrorCodesEnum.insufficientSpace) {
            const confirmDialogModel: ConfirmDialogModel = {
                inputs: {
                    heading: 'PageBuilder.error.imageLimitHeader',
                    messageHtml: 'PageBuilder.error.imageLimitMessageHtml',
                    alertType: messageSeverityEnum.error,
                    okLabel: 'PageBuilder.error.imageLimitOkLabel',
                    showCancel: false
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {

                        }
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);

            return;
        }

        if (errorCode === MediaServiceErrorCodesEnum.fileMaxSize) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.fileSizeHeader'),
                detail: this.localizationService.transform('PageBuilder.error.fileSizeMessage')
            });
            return;
        }

        if (errorCode === MediaServiceErrorCodesEnum.duplicateFile) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.imageUploadFailed'),
                detail: this.localizationService.transform('PageBuilder.error.imageUploadFailedMessageDuplicate')
            });
        } else if (errorCode !== null) {
            this.messageService.add({
                severity: 'error',
                summary: this.localizationService.transform('PageBuilder.error.imageUploadFailed'),
                detail: this.localizationService.transform('PageBuilder.error.imageUploadFailedMessage')
            });
        }
    }
}
